import { http } from '@/lib/ats-https';
import {
  BulkActivitiesResponse,
  BulkActivityItem,
} from './BulkOperationQueue.interface';

// Query keys for React Query cache management
export const BULK_ACTIVITIES_QUERY_KEYS = {
  all: ['bulk_activities'] as const,
  list: () => [...BULK_ACTIVITIES_QUERY_KEYS.all, 'list'] as const,
  // Query key for modal/queue polling with skip_viewed=true (excludes already viewed activities)
  listWithSkipViewed: () =>
    [...BULK_ACTIVITIES_QUERY_KEYS.all, 'list', 'skip_viewed'] as const,
};

// API response structure from backend
interface BulkActivitiesApiResponse {
  data: BulkActivityItem[];
  errors: Array<{ item_id?: number; error_code?: string; message?: string }>;
}

interface GetBulkActivitiesParams {
  /** If true, skip activities that have been marked as viewed */
  skip_viewed?: boolean;
}

/**
 * Fetches the list of active bulk activities from the API
 * This endpoint returns all ongoing bulk operations for the current user
 * @param params.skip_viewed - If true, excludes already viewed activities (used by BulkOperationQueue modal)
 */
const getBulkActivities = async (
  params: GetBulkActivitiesParams = {}
): Promise<BulkActivitiesResponse> => {
  const queryParams = params.skip_viewed ? { skip_viewed: true } : {};
  const response = await http.get<BulkActivitiesApiResponse>(
    '/bulk_activities',
    {
      params: queryParams,
    }
  );

  // The http client may return data directly or nested
  const apiData = response.data || response;
  const dataArray = apiData?.data || (Array.isArray(apiData) ? apiData : []);

  // Transform API response to match expected interface
  const activities: BulkActivityItem[] = [];
  const failed: BulkActivityItem[] = [];

  if (dataArray && Array.isArray(dataArray)) {
    dataArray.forEach((item: BulkActivityItem) => {
      // `automation_executed` is an audit event the BE emits when stage
      // automation fires/cancels (for the Recent Activities feed) — it's
      // not a user-initiated bulk operation, so keep it out of the queue
      // popup + nav tracker. It still shows in Recent Activities, which
      // fetches separately.
      if (item.activity === 'automation_executed') return;
      if (item.status === 'failed') {
        failed.push(item);
      } else {
        activities.push(item);
      }
    });
  }

  return {
    activities,
    failed,
  };
};

/**
 * Marks bulk activities as viewed by sending the highest completed/failed activity ID
 * This ensures that next time, activities up to this ID won't be shown again
 * @param entityId - The highest ID of completed/failed activities
 */
const markActivitiesAsViewed = async (entityId: number): Promise<void> => {
  await http.post('/entity_views', {
    viewable_type: 'BulkActivity',
    viewable_id: entityId,
  });
};

export const BULK_OPERATION_QUEUE_API_SERVICES = {
  getBulkActivities,
  markActivitiesAsViewed,
};
